import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Form, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import moment from 'moment';

const DynamicFieldRenderer = ({
  field,
  value,
  onChange,
  onFileSelect,
  existingFile,
  error,
  readOnly = false
}) => {
  const { field_key, label, type, required, options } = field;

  // State for textarea switch: "enabled" (checked) means the textarea is disabled.
  const [isTextareaDisabled, setIsTextareaDisabled] = useState(
    type === 'textarea' ? !value : false
  );

  useEffect(() => {
    if (type === 'textarea') {
      setIsTextareaDisabled(!value);
    }
  }, [type, value]);

  const handleTextChange = e => {
    onChange(e.target.value);
  };

  const handleNumberChange = e => {
    const val = e.target.value;
    onChange(val === '' ? '' : Number(val));
  };

  const handleCheckboxChange = e => {
    onChange(e.target.checked);
  };

  const handleDateChange = date => {
    onChange(date ? moment(date).format('YYYY-MM-DD') : '');
  };

  const handleSelectChange = selectedOption => {
    onChange(selectedOption ? selectedOption.value : '');
  };

  const getFileModeValue = () => {
    if (!existingFile) {
      return { mode: 'file', file_index: null };
    }

    return {
      mode: 'file',
      file_index: null,
      has_existing_file: true,
      existing_file: existingFile
    };
  };

  const renderExistingFile = () => {
    if (
      !existingFile ||
      value?.mode !== 'file' ||
      value?.has_existing_file === false
    ) {
      return null;
    }

    const fileName =
      existingFile.filename ||
      existingFile.name ||
      existingFile.original_name ||
      'Uploaded file';
    const fileUrl = existingFile.preview_url || existingFile.url || '';

    return (
      <div className="border rounded bg-light px-3 py-2 mb-2 d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-2 overflow-hidden me-2">
          <FontAwesomeIcon icon="file" className="text-primary flex-shrink-0" />
          {fileUrl ? (
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ wordBreak: 'break-all', minWidth: 0 }}
            >
              {fileName}
            </a>
          ) : (
            <span
              className="fw-semi-bold"
              style={{ wordBreak: 'break-all', minWidth: 0 }}
            >
              {fileName}
            </span>
          )}
        </div>
        <span className="text-500 fs-11 flex-shrink-0">Uploaded</span>
      </div>
    );
  };
  const renderControl = () => {
    if (readOnly) {
      if (type === 'checkbox') {
        return (
          <Form.Check
            type="checkbox"
            checked={!!value}
            disabled
            label={label}
          />
        );
      }
      return (
        <Form.Control
          type="text"
          value={
            type === 'date' && value
              ? moment(value).format('MM/DD/YYYY')
              : value !== undefined && value !== null
              ? String(value)
              : ''
          }
          disabled
        />
      );
    }

    switch (type) {
      case 'textarea':
        return (
          <div>
            {!readOnly && (
              <Form.Check
                type="switch"
                id={`disable-switch-${field_key}`}
                label={
                  isTextareaDisabled ? 'Click to Enable' : 'Click to Disable'
                }
                checked={isTextareaDisabled}
                onChange={e => {
                  setIsTextareaDisabled(e.target.checked);
                  if (e.target.checked) {
                    onChange('');
                  }
                }}
                className="mb-2"
              />
            )}
            <Form.Control
              as="textarea"
              rows={3}
              value={value || ''}
              onChange={handleTextChange}
              isInvalid={!!error}
              disabled={readOnly || isTextareaDisabled}
            />
          </div>
        );
      case 'number':
        return (
          <Form.Control
            type="number"
            value={value !== undefined && value !== null ? value : ''}
            onChange={handleNumberChange}
            isInvalid={!!error}
          />
        );
      case 'date':
        return (
          <div className="w-100">
            <DatePicker
              selected={value ? moment(value, 'YYYY-MM-DD').toDate() : null}
              onChange={handleDateChange}
              className={`form-control ${error ? 'is-invalid' : ''}`}
              dateFormat="MM/dd/yyyy"
              placeholderText="Select date"
            />
            {error && <div className="invalid-feedback d-block">{error}</div>}
          </div>
        );
      case 'select': {
        const selectOptions = (options || []).map(opt => ({
          value: opt,
          label: opt
        }));
        const selectedValue =
          selectOptions.find(opt => opt.value === value) || null;
        return (
          <Select
            options={selectOptions}
            value={selectedValue}
            onChange={handleSelectChange}
            classNamePrefix="react-select"
            isClearable
            className={error ? 'is-invalid' : ''}
          />
        );
      }
      case 'radio':
        return (
          <div className="d-flex flex-wrap gap-3 mt-1">
            {(options || []).map((opt, idx) => (
              <Form.Check
                key={idx}
                type="radio"
                id={`${field_key}-${idx}`}
                label={opt}
                name={field_key}
                checked={value === opt}
                onChange={() => onChange(opt)}
                isInvalid={!!error}
                inline
              />
            ))}
          </div>
        );
      case 'checkbox':
        return (
          <Form.Check
            type="checkbox"
            id={field_key}
            label={label}
            checked={!!value}
            onChange={handleCheckboxChange}
            isInvalid={!!error}
          />
        );
      case 'file':
        if (readOnly) {
          return (
            <div className="text-muted">
              <em>
                File attachment (
                {value?.mode === 'file' ? 'Uploaded' : 'Pending'})
              </em>
            </div>
          );
        }
        return (
          <div className="w-100">
            {renderExistingFile()}
            <Form.Control
              type="file"
              onChange={e => {
                const file = e.target.files[0];
                if (file && onFileSelect) {
                  onFileSelect(field_key, file);
                }
              }}
              isInvalid={!!error}
              disabled={readOnly}
            />
          </div>
        );
      case 'file_or_text': {
        const mode = value?.mode || 'text';
        if (readOnly) {
          if (mode === 'text') {
            return (
              <Form.Control
                as="textarea"
                rows={2}
                value={value?.value || ''}
                disabled
              />
            );
          }
          return (
            <div className="text-muted">
              <em>
                File attachment (
                {value?.mode === 'file' ? 'Uploaded' : 'Pending'})
              </em>
            </div>
          );
        }
        return (
          <div className="w-100">
            <div className="d-flex gap-2 mb-2">
              <Button
                variant={mode === 'text' ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => onChange({ mode: 'text', value: '' })}
              >
                <FontAwesomeIcon icon="pen" className="me-2" /> Describe
              </Button>
              <Button
                variant={mode === 'file' ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => onChange(getFileModeValue())}
              >
                <FontAwesomeIcon icon="paperclip" className="me-2" /> Upload
                File
              </Button>
            </div>

            {mode === 'text' && (
              <Form.Control
                as="textarea"
                rows={2}
                value={value?.value || ''}
                onChange={e =>
                  onChange({ mode: 'text', value: e.target.value })
                }
                placeholder={`Describe ${label.toLowerCase()}...`}
                isInvalid={!!error}
                disabled={readOnly}
              />
            )}

            {mode === 'file' && (
              <>
                {renderExistingFile()}
                <Form.Control
                  type="file"
                  onChange={e => {
                    const file = e.target.files[0];
                    if (file && onFileSelect) {
                      onFileSelect(field_key, file);
                    }
                  }}
                  isInvalid={!!error}
                  disabled={readOnly}
                />
              </>
            )}
          </div>
        );
      }
      case 'text':
      default:
        return (
          <Form.Control
            type="text"
            value={value || ''}
            onChange={handleTextChange}
            isInvalid={!!error}
            disabled={readOnly}
          />
        );
    }
  };

  return (
    <Form.Group className="mb-3" controlId={field_key}>
      {type !== 'checkbox' && (
        <Form.Label className="fw-semi-bold">
          {label} {required ? <span className="text-danger">*</span> : ''}
        </Form.Label>
      )}
      {renderControl()}
      {error && type !== 'date' && (
        <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
      )}
    </Form.Group>
  );
};

DynamicFieldRenderer.propTypes = {
  field: PropTypes.shape({
    field_key: PropTypes.string.isRequired,
    label: PropTypes.string,
    type: PropTypes.oneOf([
      'text',
      'textarea',
      'number',
      'date',
      'select',
      'radio',
      'checkbox',
      'file',
      'file_or_text'
    ]).isRequired,
    required: PropTypes.bool,
    options: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  onFileSelect: PropTypes.func,
  existingFile: PropTypes.shape({
    id: PropTypes.number,
    file_id: PropTypes.number,
    file_index: PropTypes.number,
    filename: PropTypes.string,
    name: PropTypes.string,
    original_name: PropTypes.string,
    preview_url: PropTypes.string,
    url: PropTypes.string
  }),
  error: PropTypes.string,
  readOnly: PropTypes.bool
};

export default DynamicFieldRenderer;
